import { useState } from 'react';

/**
 * design-system/06-Components/01-Buttons.md — secondary: raised surface, `action/secondary` label, a 1dp
 * `border/strong` edge. An alternative that is not the point of the screen.
 * Mirrors android/…/Components/SecondaryButton.kt and ios/…/Components/SecondaryButton.swift.
 *
 * The system has specified this since it was written and nothing implemented it, which cost twice:
 * the welcome screen drew its own copy inline, and R0069's session error screen ended up with two
 * Primaries — against the rule three lines below the table, that a screen has exactly one Primary and a
 * second action is Secondary.
 *
 * A real `<button>`, for the same reason as Primary: a styled `<div>` loses focus, role and the enter key.
 */
export function SecondaryButton({
  label,
  onClick,
  enabled = true,
  height = 'var(--touch-child)',
  fullWidth = true,
  type = 'button',
}: {
  label: string;
  onClick?: () => void;
  enabled?: boolean;
  height?: string;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
}) {
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type={type}
      disabled={!enabled}
      onClick={onClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        minHeight: height,
        width: fullWidth ? '100%' : undefined,
        padding: 'var(--space-3) var(--space-6)',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--border-strong)',
        background: pressed ? 'var(--surface-sunken)' : 'var(--surface-raised)',
        color: enabled ? 'var(--action-secondary)' : 'var(--text-disabled)',
        fontSize: '15px',
        lineHeight: '20px',
        fontWeight: 600,
        cursor: enabled ? 'pointer' : 'default',
        transition: 'background var(--motion-quick) ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {label}
    </button>
  );
}
